<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FormImageUploadField from './FormImageUploadField.vue';

defineProps({
  formId: { type: [String, Number], default: null },
});

const { t } = useI18n();

const theme = defineModel({ type: Object, required: true });

const updateTheme = (key, value) => {
  theme.value = { ...theme.value, [key]: value };
};

// Altura do logo/capa em px. Aceita número ou os rótulos antigos
// (small/medium/large) por compatibilidade com forms já salvos.
const LEGACY_HEADER_SIZES = { small: 96, medium: 150, large: 240 };
const headerSizePx = computed(() => {
  const value = theme.value.header_size;
  if (typeof value === 'number') return value;
  return LEGACY_HEADER_SIZES[value] ?? 72;
});
const headerSizeLabel = computed(() => `${headerSizePx.value}px`);

// Layout is a rendering choice only — same fields/submission contract either
// way. Stored under theme.layout (JSONB) so it travels with the rest of the
// form's appearance and needed no schema migration.
const currentLayout = computed(() => theme.value.layout || 'single');
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('FORMS_MGMT.EDITOR.APPEARANCE.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('FORMS_MGMT.EDITOR.APPEARANCE.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('FORMS_MGMT.EDITOR.APPEARANCE.LAYOUT') }}
      </span>
      <p class="text-sm text-n-slate-11 -mt-1">
        {{ t('FORMS_MGMT.EDITOR.APPEARANCE.LAYOUT_DESCRIPTION') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <Button
          icon="i-lucide-layout-list"
          size="sm"
          :faded="currentLayout !== 'single'"
          :slate="currentLayout !== 'single'"
          :blue="currentLayout === 'single'"
          :label="t('FORMS_MGMT.EDITOR.APPEARANCE.LAYOUT_SINGLE')"
          @click="updateTheme('layout', 'single')"
        />
        <Button
          icon="i-lucide-list-ordered"
          size="sm"
          :faded="currentLayout !== 'steps'"
          :slate="currentLayout !== 'steps'"
          :blue="currentLayout === 'steps'"
          :label="t('FORMS_MGMT.EDITOR.APPEARANCE.LAYOUT_STEPS')"
          @click="updateTheme('layout', 'steps')"
        />
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-4">
        <div class="grid grid-cols-2 gap-3">
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.APPEARANCE.PRIMARY_COLOR') }}
            </span>
            <ColorPicker
              :model-value="theme.primary_color"
              @update:model-value="value => updateTheme('primary_color', value)"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.APPEARANCE.BUTTON_COLOR') }}
            </span>
            <ColorPicker
              :model-value="theme.button_color"
              @update:model-value="value => updateTheme('button_color', value)"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.APPEARANCE.BUTTON_TEXT_COLOR') }}
            </span>
            <ColorPicker
              :model-value="theme.button_text_color"
              @update:model-value="
                value => updateTheme('button_text_color', value)
              "
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.APPEARANCE.BACKGROUND_COLOR') }}
            </span>
            <ColorPicker
              :model-value="theme.background_color"
              @update:model-value="
                value => updateTheme('background_color', value)
              "
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.APPEARANCE.CARD_COLOR') }}
            </span>
            <ColorPicker
              :model-value="theme.card_color"
              @update:model-value="value => updateTheme('card_color', value)"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.APPEARANCE.TEXT_COLOR') }}
            </span>
            <ColorPicker
              :model-value="theme.text_color"
              @update:model-value="value => updateTheme('text_color', value)"
            />
          </label>
        </div>

        <FormImageUploadField
          :model-value="theme.logo_url"
          :form-id="formId"
          kind="logo"
          :label="t('FORMS_MGMT.EDITOR.APPEARANCE.LOGO')"
          :hint="t('FORMS_MGMT.EDITOR.APPEARANCE.LOGO_HINT')"
          @update:model-value="value => updateTheme('logo_url', value)"
        />
        <FormImageUploadField
          :model-value="theme.header_image_url"
          :form-id="formId"
          kind="header"
          :label="t('FORMS_MGMT.EDITOR.APPEARANCE.HEADER_IMAGE')"
          :hint="t('FORMS_MGMT.EDITOR.APPEARANCE.HEADER_IMAGE_HINT')"
          @update:model-value="value => updateTheme('header_image_url', value)"
        />

        <div v-if="theme.header_image_url" class="flex flex-col gap-1">
          <span
            class="flex items-center justify-between text-sm font-medium text-n-slate-12"
          >
            {{ t('FORMS_MGMT.EDITOR.APPEARANCE.HEADER_SIZE') }}
            <span class="text-xs font-normal text-n-slate-11 tabular-nums">
              {{ headerSizeLabel }}
            </span>
          </span>
          <input
            :value="headerSizePx"
            type="range"
            min="32"
            max="260"
            step="4"
            class="w-full accent-n-brand cursor-pointer"
            @input="
              event => updateTheme('header_size', Number(event.target.value))
            "
          />
        </div>

        <label
          v-if="theme.header_image_url"
          class="flex items-start gap-2 cursor-pointer"
        >
          <input
            type="checkbox"
            class="mt-0.5"
            :checked="theme.header_on_form"
            @change="updateTheme('header_on_form', $event.target.checked)"
          />
          <span class="text-sm text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.APPEARANCE.HEADER_ON_FORM') }}
          </span>
        </label>

        <div class="grid grid-cols-2 gap-3">
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.APPEARANCE.CORNER_RADIUS') }}
            </span>
            <input
              :value="theme.radius"
              type="number"
              min="0"
              max="32"
              class="form-input"
              @input="
                event => updateTheme('radius', Number(event.target.value))
              "
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.APPEARANCE.FONT') }}
            </span>
            <select
              :value="theme.font"
              class="form-select"
              @change="event => updateTheme('font', event.target.value)"
            >
              <option value="sans">
                {{ t('FORMS_MGMT.EDITOR.APPEARANCE.FONT_SANS') }}
              </option>
              <option value="serif">
                {{ t('FORMS_MGMT.EDITOR.APPEARANCE.FONT_SERIF') }}
              </option>
            </select>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
